/**
 * Timeline Component
 * 
 * Horizontal timeline with clips and playhead.
 * Simplified version without drag-drop (can be added later with dnd-kit).
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { TimelineProps } from '../types';
import { formatTime, calculatePixelsPerSecond } from '../utils/clipUtils';
import TimelineClip from './TimelineClip';

const Timeline: React.FC<TimelineProps> = React.memo(({
  clips,
  selectedClipId,
  playheadPosition,
  zoomLevel,
  totalDuration,
  onClipSelect,
  onClipReorder,
  onClipTrim,
  onPlayheadChange,
  onSplit,
  onDelete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 48);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Calculate pixels per second
  const pixelsPerSecond = calculatePixelsPerSecond(
    containerWidth,
    Math.max(totalDuration, 10),
    zoomLevel
  );

  // Track width based on content
  const trackWidth = Math.max(containerWidth, totalDuration * pixelsPerSecond);

  // Handle track click to move playhead and deselect clips
  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (!trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newPosition = x / pixelsPerSecond;
    onPlayheadChange(Math.max(0, Math.min(newPosition, totalDuration)));
    
    // Deselect any selected clip when clicking empty track area
    onClipSelect(null);
  }, [pixelsPerSecond, totalDuration, onPlayheadChange, onClipSelect]);

  // Handle playhead drag
  const handlePlayheadMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPlayhead(true);
  }, []);

  useEffect(() => {
    if (!isDraggingPlayhead) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const newPosition = x / pixelsPerSecond;
      onPlayheadChange(Math.max(0, Math.min(newPosition, totalDuration)));
    };

    const handleMouseUp = () => {
      setIsDraggingPlayhead(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPlayhead, pixelsPerSecond, totalDuration, onPlayheadChange]);

  // Generate time ruler marks
  const rulerMarks = [];
  const interval = zoomLevel < 1 ? 10 : zoomLevel < 2 ? 5 : 1;
  for (let t = 0; t <= totalDuration + interval; t += interval) {
    rulerMarks.push(t);
  }

  // Playhead position in pixels
  const playheadX = playheadPosition * pixelsPerSecond;

  // Move clip up/down in order
  const moveClip = useCallback((clipId: string, direction: 'up' | 'down') => {
    const index = clips.findIndex(c => c.id === clipId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= clips.length) return;
    
    const newOrder = [...clips.map(c => c.id)];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    onClipReorder(newOrder);
  }, [clips, onClipReorder]);

  return (
    <div ref={containerRef} className="relative flex flex-col h-full bg-gray-50 select-none overflow-visible">
      {/* Time Ruler */}
      <div className="h-6 bg-white border-b border-gray-200 overflow-hidden">
        <div className="relative h-full" style={{ width: trackWidth }}>
          {rulerMarks.map((time) => (
            <div
              key={time}
              className="absolute top-0 h-full flex flex-col items-center"
              style={{ left: time * pixelsPerSecond }}
            >
              <div className="w-px h-2 bg-gray-300" />
              <span className="text-xs text-gray-400 mt-0.5">{formatTime(time)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Track Area */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div
          ref={trackRef}
          className="relative h-full min-h-[100px]"
          style={{ width: trackWidth }}
          onClick={handleTrackClick}
        >
          {/* Track Background */}
          <div className="absolute inset-0 bg-gray-100">
            {rulerMarks.map((time) => (
              <div
                key={time}
                className="absolute top-0 bottom-0 w-px bg-gray-200"
                style={{ left: time * pixelsPerSecond }}
              />
            ))}
          </div>

          {/* Clips Track */}
          <div className="absolute top-4 left-0 right-0 h-16">
            <div className="flex h-full">
              {clips.map((clip, index) => {
                const clipWidth = (clip.endTime - clip.startTime) * pixelsPerSecond;
                return (
                  <TimelineClip
                    key={clip.id}
                    clip={clip}
                    width={clipWidth}
                    isSelected={clip.id === selectedClipId}
                    pixelsPerSecond={pixelsPerSecond}
                    onSelect={() => onClipSelect(clip.id)}
                    onTrim={(startDelta, endDelta) => {
                      const newStart = Math.max(0, clip.startTime + startDelta);
                      const newEnd = Math.max(newStart + 0.5, clip.endTime + endDelta);
                      onClipTrim(clip.id, newStart, newEnd);
                    }}
                    onMoveUp={index > 0 ? () => moveClip(clip.id, 'up') : undefined}
                    onMoveDown={index < clips.length - 1 ? () => moveClip(clip.id, 'down') : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* Playhead */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 cursor-col-resize"
            style={{ left: playheadX }}
            onMouseDown={handlePlayheadMouseDown}
          >
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-500 rotate-45" />
          </div>

          {/* Empty State */}
          {clips.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-gray-400 text-sm">Select clips from source panel to add to timeline</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected Clip Inspector - Floating panel that expands below */}
      {selectedClipId && (() => {
        const selectedClip = clips.find(c => c.id === selectedClipId);
        if (!selectedClip) return null;
        const duration = selectedClip.endTime - selectedClip.startTime;
        const hasMarker = selectedClip.markerTime !== undefined;
        const rangeBefore = hasMarker ? Math.round(selectedClip.markerTime! - selectedClip.startTime) : 0;
        const rangeAfter = hasMarker ? Math.round(selectedClip.endTime - selectedClip.markerTime!) : 0;
        
        return (
          <div className="absolute left-0 right-0 bottom-0 h-8 bg-white border-t border-gray-200 px-4 flex items-center space-x-4 text-xs z-30 transform translate-y-full shadow-sm">
            <button
              onClick={() => onClipSelect(null)}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              title="Deselect clip"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <span className="text-gray-700 font-medium truncate max-w-32">{selectedClip.label || 'Clip'}</span>
            <div className="flex items-center space-x-2">
              {hasMarker ? (
                <>
                  <span className="text-gray-500">Range:</span>
                  <input
                    type="number"
                    step="1"
                    value={rangeBefore}
                    onChange={(e) => {
                      const newBefore = parseFloat(e.target.value);
                      if (Number.isNaN(newBefore)) return;
                      const computedStart = selectedClip.markerTime! - newBefore;
                      const clampedStart = Math.min(selectedClip.endTime - 0.5, Math.max(0, computedStart));
                      onClipTrim(selectedClipId, clampedStart, selectedClip.endTime);
                    }}
                    className="w-10 px-1 py-0.5 text-center bg-gray-50 border border-gray-200 rounded text-gray-700 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400">s before /</span>
                  <input
                    type="number"
                    step="1"
                    value={rangeAfter}
                    onChange={(e) => {
                      const newAfter = parseFloat(e.target.value);
                      if (Number.isNaN(newAfter)) return;
                      const computedEnd = selectedClip.markerTime! + newAfter;
                      const clampedEnd = Math.max(selectedClip.startTime + 0.5, computedEnd);
                      onClipTrim(selectedClipId, selectedClip.startTime, clampedEnd);
                    }}
                    className="w-10 px-1 py-0.5 text-center bg-gray-50 border border-gray-200 rounded text-gray-700 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400">s after</span>
                </>
              ) : (
                <>
                  <span className="text-gray-500">Start:</span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={selectedClip.startTime.toFixed(1)}
                    onChange={(e) => {
                      const newStart = Math.max(0, parseFloat(e.target.value) || 0);
                      if (newStart < selectedClip.endTime - 0.5) {
                        onClipTrim(selectedClipId, newStart, selectedClip.endTime);
                      }
                    }}
                    className="w-14 px-1 py-0.5 text-center bg-gray-50 border border-gray-200 rounded text-gray-700 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-500">End:</span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={selectedClip.endTime.toFixed(1)}
                    onChange={(e) => {
                      const newEnd = Math.max(selectedClip.startTime + 0.5, parseFloat(e.target.value) || 0);
                      onClipTrim(selectedClipId, selectedClip.startTime, newEnd);
                    }}
                    className="w-14 px-1 py-0.5 text-center bg-gray-50 border border-gray-200 rounded text-gray-700 focus:outline-none focus:border-blue-500"
                  />
                </>
              )}
              <span className="text-gray-400 ml-2">Duration:</span>
              <span className="text-gray-600">{formatTime(duration)}</span>
              
              {/* Clip Actions */}
              <div className="flex items-center space-x-1 ml-4 border-l border-gray-200 pl-4">
                {onSplit && (
                  <button
                    onClick={onSplit}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    title="Split at Playhead"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M12 2v20M12 2l-3 3M12 2l3 3" strokeLinecap="round" strokeLinejoin="round" />
                      <rect x="3" y="6" width="6" height="12" rx="1" strokeLinecap="round" />
                      <rect x="15" y="6" width="6" height="12" rx="1" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={onDelete}
                    className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete Clip"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Timeline Info Bar */}
      <div className="h-6 bg-white border-t border-gray-200 px-4 flex items-center justify-between text-xs text-gray-500">
        <span>
          {clips.length} clip{clips.length !== 1 ? 's' : ''} • {formatTime(totalDuration)} total
        </span>
        <span>
          Playhead: {formatTime(playheadPosition)}
        </span>
      </div>
    </div>
  );
});

Timeline.displayName = 'Timeline';

export default Timeline;
